/**
 * R7.2.2B — Customer production boot attestation (no PII).
 */
import { emitEvent } from "./events-v2.js";

export type ProductionReleaseMeta = {
  surface: string;
  release_tag: string;
  commit_sha: string | null;
  artifact_hash: string | null;
  built_at: string;
  deployment_id: string | null;
  build_id: string | null;
  bundle: string | null;
  api_release_expected?: {
    release_tag?: string;
    worker_marker?: string;
    commit_sha?: string | null;
    api_origin?: string;
  };
};

export type WorkerRuntimeMeta = {
  ok?: boolean;
  release_tag?: string;
  worker_marker?: string;
  commit_sha?: string | null;
};

const ATTESTATION_SENT_KEY = "asiastyle_customer_release_attestation_v1";

function mainBundleHashFromScript(): string | null {
  try {
    const scripts = Array.from(document.querySelectorAll("script[src*='assets/index-']"));
    const src = scripts
      .map((s) => s.getAttribute("src") ?? "")
      .find((v) => /assets\/index-/.test(v));
    return src ? src.split("?")[0] ?? null : null;
  } catch {
    return null;
  }
}

async function fetchReleaseMeta(): Promise<ProductionReleaseMeta | null> {
  try {
    const res = await fetch("/__release.json", { cache: "no-store" });
    if (!res.ok) return null;
    return (await res.json()) as ProductionReleaseMeta;
  } catch {
    return null;
  }
}

async function fetchWorkerMeta(apiBase: string): Promise<WorkerRuntimeMeta | null> {
  try {
    const base = apiBase.replace(/\/$/, "");
    const res = await fetch(`${base}/api/runtime-meta`, { cache: "no-store" });
    if (!res.ok) return null;
    return (await res.json()) as WorkerRuntimeMeta;
  } catch {
    return null;
  }
}

export async function recordCustomerProductionBootAttestation(apiBase: string): Promise<void> {
  try {
    if (sessionStorage.getItem(ATTESTATION_SENT_KEY) === "1") return;
    const [releaseMeta, workerMeta] = await Promise.all([
      fetchReleaseMeta(),
      fetchWorkerMeta(apiBase),
    ]);
    const payload = {
      surface: "customer",
      release_tag: releaseMeta?.release_tag ?? null,
      asset_hash: releaseMeta?.artifact_hash ?? mainBundleHashFromScript(),
      host: window.location.host,
      build_id: releaseMeta?.build_id ?? null,
      worker_release_received: workerMeta
        ? {
            release_tag: workerMeta.release_tag ?? null,
            worker_marker: workerMeta.worker_marker ?? null,
            commit_sha: workerMeta.commit_sha ?? null,
          }
        : null,
      timestamp: new Date().toISOString(),
    };
    emitEvent("production_build_attestation", payload);
    void fetch(`${apiBase.replace(/\/$/, "")}/api/release-attestation`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => undefined);
    sessionStorage.setItem(ATTESTATION_SENT_KEY, "1");
  } catch {
    /* never break boot */
  }
}
